import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { MultiSelect } from "react-multi-select-component";
import {
  Card,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";

function NewsEditor({
  title,
  submitLabel,
  initialValues,
  onSubmit,
  submitLoading,
  notification,
  notificationColor,
  notificationMessage,
}) {
  const [form, setForm] = useState({
    news_title: "",
    summary: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    status: "active",
    news_content: "",
    news_poster: null,
    existingPoster: "",
    related_testseries: [],
  });
  const [testSeriesOptions, setTestSeriesOptions] = useState([]);

  useEffect(() => {
    if (initialValues) {
      setForm({
        news_title: initialValues.news_title || "",
        summary: initialValues.summary || "",
        seo_title: initialValues.seo_title || "",
        seo_description: initialValues.seo_description || "",
        seo_keywords: initialValues.seo_keywords || "",
        status: initialValues.status || "active",
        news_content: initialValues.news_content || "",
        news_poster: null,
        existingPoster: initialValues.news_poster || "",
        related_testseries: (initialValues.related_testseries || []).map((item) => ({
          label: item.test_series_name,
          value: item._id,
        })),
      });
    }
  }, [initialValues]);

  useEffect(() => {
    const loadTestSeries = async () => {
      if (init() !== "success") {
        return;
      }

      try {
        const response = await axios.get(`${Constants.getUrls.getAllTestSeries}?limit=200`);
        const options = (response?.data?.data?.docs || []).map((item) => ({
          label: `${item.test_series_name} (${item.test_series_type || "scheduled"})`,
          value: item._id,
        }));
        setTestSeriesOptions(options);
      } catch (error) {
        console.log(error);
      }
    };

    loadTestSeries();
  }, []);

  const posterPreview = useMemo(() => {
    if (form.news_poster) {
      return URL.createObjectURL(form.news_poster);
    }

    if (form.existingPoster) {
      return `${Constants.imageDomain}${form.existingPoster}`;
    }

    return "";
  }, [form.news_poster, form.existingPoster]);

  useEffect(
    () => () => {
      if (form.news_poster && posterPreview.startsWith("blob:")) {
        URL.revokeObjectURL(posterPreview);
      }
    },
    [form.news_poster, posterPreview]
  );

  const updateField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(form);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              {title}
            </MDTypography>
          </MDBox>
          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InputLabel>News Title</InputLabel>
                  <TextField
                    fullWidth
                    required
                    value={form.news_title}
                    onChange={(e) => updateField("news_title", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    fullWidth
                    value={form.status}
                    onChange={(e) => updateField("status", e.target.value)}
                    style={{ height: "45px", color: "#111" }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </Grid>

                <Grid item xs={12} md={6}>
                  <InputLabel>News Poster</InputLabel>
                  <TextField
                    fullWidth
                    type="file"
                    focused
                    helperText="Recommended landscape image for listing and detail hero."
                    onChange={(e) => updateField("news_poster", e.target.files?.[0] || null)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel>Related Test Series</InputLabel>
                  <MultiSelect
                    options={testSeriesOptions}
                    value={form.related_testseries}
                    onChange={(value) => updateField("related_testseries", value)}
                    labelledBy="Select related test series"
                  />
                </Grid>

                <Grid item xs={12}>
                  <InputLabel>Quick Summary</InputLabel>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={form.summary}
                    onChange={(e) => updateField("summary", e.target.value)}
                    helperText="Short answer-style summary for listing cards, SEO snippets, and answer engines."
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputLabel>SEO Title</InputLabel>
                  <TextField
                    fullWidth
                    value={form.seo_title}
                    onChange={(e) => updateField("seo_title", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InputLabel>SEO Description</InputLabel>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.seo_description}
                    onChange={(e) => updateField("seo_description", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InputLabel>SEO Keywords</InputLabel>
                  <TextField
                    fullWidth
                    value={form.seo_keywords}
                    onChange={(e) => updateField("seo_keywords", e.target.value)}
                    helperText="Comma-separated keywords"
                  />
                </Grid>

                <Grid item xs={12}>
                  <InputLabel>News Content</InputLabel>
                  <SunEditor
                    height="420px"
                    setContents={form.news_content}
                    onChange={(content) => updateField("news_content", content)}
                    setOptions={{
                      buttonList: [
                        ["undo", "redo"],
                        ["formatBlock", "fontSize", "bold", "underline", "italic", "strike"],
                        ["fontColor", "hiliteColor", "align", "list", "table"],
                        ["link", "image", "video"],
                        ["removeFormat", "codeView"],
                      ],
                    }}
                  />
                </Grid>

                {posterPreview ? (
                  <Grid item xs={12}>
                    <MDBox
                      component="img"
                      src={posterPreview}
                      alt="News preview"
                      sx={{
                        width: "100%",
                        maxWidth: 420,
                        borderRadius: "18px",
                        border: "1px solid rgba(24,53,38,0.12)",
                      }}
                    />
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <MDButton type="submit" variant="contained" color="primary" disabled={submitLoading}>
                    {submitLoading ? "Saving..." : submitLabel}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
          </MDBox>
          <MDSnackbar
            color={notificationColor}
            icon="notifications"
            title="AgriQuiz"
            content={notificationMessage}
            dateTime="Just now"
            open={notification}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

NewsEditor.propTypes = {
  title: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  submitLoading: PropTypes.bool,
  notification: PropTypes.bool,
  notificationColor: PropTypes.string,
  notificationMessage: PropTypes.string,
};

NewsEditor.defaultProps = {
  initialValues: null,
  submitLoading: false,
  notification: false,
  notificationColor: "success",
  notificationMessage: "",
};

export default NewsEditor;
